import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: inline;
      font-family: var(--uwc-font-family, inherit);
    }

    /* ── Base anchor ─────────────────────────────────────────────────────────── */
    .uwc-link {
      display: inline-flex;
      align-items: center;
      gap: 0.3em;
      position: relative;
      color: var(--uwc-link-color, var(--uwc-color-primary, ${t.primary}));
      font-weight: var(--uwc-link-font-weight, ${t.fontWeightBold});
      font-size: var(--uwc-link-font-size, inherit);
      text-decoration: none;
      cursor: pointer;
      line-height: 1.4;
      border-radius: ${t.radiusSm};
      padding: 0.05em 0.25em;
      margin: 0 -0.25em;
      background-color: transparent;
      transition:
        color var(--uwc-duration-base, ${t.durationBase}) ease,
        background-color var(--uwc-duration-base, ${t.durationBase}) ease;
    }

    .uwc-link:hover {
      background-color: var(--uwc-link-hover-bg, color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 10%, transparent));
    }

    /* ── Always-on base underline (thin, semi-transparent) ───────────────────── */
    .uwc-link::before {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 1px;
      background: currentColor;
      opacity: 0.3;
    }

    /* ── Animated overlay underline — draws left-to-right on hover ───────────── */
    .uwc-link::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 1px;
      background: currentColor;
      transform: scaleX(0);
      transform-origin: left center;
      transition: transform var(--uwc-duration-slow, ${t.durationslow}) var(--uwc-easing, ${t.easingStandard});
    }

    .uwc-link:hover::after,
    .uwc-link:focus-visible::after {
      transform: scaleX(1);
    }

    /* ── Icon ────────────────────────────────────────────────────────────────── */
    .uwc-link__icon {
      flex-shrink: 0;
      display: inline-flex;
      transition: transform var(--uwc-duration-base, ${t.durationBase}) var(--uwc-easing, ${t.easingStandard});
    }

    .uwc-link:hover .uwc-link__icon--suffix {
      transform: translateX(3px);
    }

    .uwc-link:hover .uwc-link__icon--prefix {
      transform: translateX(-2px);
    }

    /* ── Focus ring ──────────────────────────────────────────────────────────── */
    .uwc-link:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 3px;
      border-radius: ${t.radiusXs};
    }

    /* ── Size variants ───────────────────────────────────────────────────────── */
    :host([size="small"])  .uwc-link { font-size: var(--uwc-font-size-sm, ${t.fontSizeSm}); }
    :host([size="medium"]) .uwc-link { font-size: var(--uwc-font-size-md, ${t.fontSizeMd}); }
    :host([size="large"])  .uwc-link { font-size: var(--uwc-font-size-lg, ${t.fontSizeLg}); }

    /* ── Color variants ──────────────────────────────────────────────────────── */
    :host([variant="secondary"]) .uwc-link {
      color: var(--uwc-link-secondary-color, var(--uwc-text-secondary, ${t.textSecondary}));
    }
    :host([variant="muted"]) .uwc-link {
      color: var(--uwc-link-muted-color, var(--uwc-text-muted, ${t.textMuted}));
    }

    /* ── Disabled ────────────────────────────────────────────────────────────── */
    .uwc-link--disabled {
      color: var(--uwc-text-disabled, ${t.textDisabled}) !important;
      cursor: not-allowed;
      pointer-events: none;
    }
    .uwc-link--disabled::before { opacity: 0.2; }
    .uwc-link--disabled::after  { display: none; }
    .uwc-link--disabled .uwc-link__icon { transform: none !important; }

    /* ── Reduced motion ──────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-link::after,
      .uwc-link__icon { transition: none; }
    }
  `,
];
